import React, { useState, useContext, useEffect } from 'react'
import { Box, Tabs, Tab, Stack, IconButton } from '@mui/material'
import { Icon } from '@cx/ui'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { grayBg } from '@/constant'
import dayjs from 'dayjs'
import { getOrgInfo } from '@/api'
import { AuthContext } from '@/layout/Auth'
import { Card, Text2, TextWhite2 } from './common'
import Order from './Order'
import UserManage from './UserManage'
import APIToken from './APIToken'
import ExitSpace from './ExitSpace'
import ExitSpaceModal from './ExitSpaceModal'
import DissolutionSpaceModal from './DissolutionSpaceModal'
import DissolutionSpace from './DissolutionSpace'
import EditSpaceNameModal from './EditSpaceNameModal'
import spaceBg from '@/asset/img/space_bg.png'

export enum TabValue {
  Base = 'base',
  Order = 'order',
}

const Personal = () => {
  const { authInfo, updateAuthInfo } = useContext(AuthContext)
  const { id } = useParams() as { id: TabValue }
  const orgInfo = authInfo?.orgInfo
  const navigate = useNavigate()
  const [value, setValue] = useState<TabValue>(id || TabValue.Base)

  const [editSpaceNameModalVisible, setEditSpaceNameModalVisible] = useState(false)
  const [exitSpaceModalVisible, setExitSpaceModalVisible] = useState(false)
  const [dissolutionSpaceModalVisible, setDissolutionSpaceModalVisible] = useState(false)
  const handleChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setValue(newValue)
    navigate(`/console/space/${newValue}`)
  }

  const getOrgInfoQuery = () => {
    return getOrgInfo(orgInfo!.id!).then((result) => {
      const newOrg = { ...orgInfo, ...result }
      updateAuthInfo({ orgInfo: newOrg })
    })
  }

  useEffect(() => {
    if (orgInfo?.id) {
      getOrgInfoQuery()
    }
  }, [orgInfo?.id])

  useEffect(() => {
    if (id !== TabValue.Base && id !== TabValue.Order) {
      navigate(`/console/space/${TabValue.Base}`)
      setValue(TabValue.Base)
    } else {
      setValue(id)
    }
  }, [id])

  return (
    <Box
      id='space-scroll'
      sx={{
        pt: 13,
        backgroundColor: grayBg,
        height: '100vh',
        overflowY: 'auto',
        pb: 5,
      }}
    >
      <EditSpaceNameModal
        open={editSpaceNameModalVisible}
        onOk={getOrgInfoQuery}
        onCancel={() => {
          setEditSpaceNameModalVisible(false)
        }}
      />
      <ExitSpaceModal
        open={exitSpaceModalVisible}
        onCancel={() => {
          setExitSpaceModalVisible(false)
        }}
      />

      <DissolutionSpaceModal
        open={dissolutionSpaceModalVisible}
        onCancel={() => {
          setDissolutionSpaceModalVisible(false)
        }}
      />

      <Stack gap={3} sx={{ width: 1120, mx: 'auto' }}>
        <Card sx={{ p: 0 }}>
          <Box
            sx={{
              position: 'relative',
              height: 104,
              backgroundSize: '100% 100%',
              backgroundImage: `url(${spaceBg})`,
              borderRadius: '8px 8px 0px 0px',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <Stack direction='row' justifyContent='space-between' sx={{ pt: '50px', pl: 5, pr: 3 }}>
              <Box
                sx={{
                  color: '#fff',
                  position: 'absolute',
                  bottom: 16,
                  left: 40,
                }}
              >
                <Stack direction='row' alignItems='center' gap={1}>
                  <Box sx={{ fontSize: 26, fontWeight: 600 }}>{orgInfo?.name}</Box>
                  <IconButton
                    size='small'
                    onClick={() => {
                      setEditSpaceNameModalVisible(true)
                    }}
                  >
                    <Icon
                      type='icon-bianji'
                      sx={{
                        color: 'rgba(255,255,255, 0.6)',
                        '&:hover': {
                          color: '#fff',
                        },
                      }}
                    ></Icon>
                  </IconButton>
                </Stack>
                <Text2 sx={{ color: 'rgba(255,255,255,0.5)' }}> {orgInfo?.id}</Text2>
              </Box>

              <Stack
                direction='row'
                sx={{ position: 'absolute', bottom: 16, right: 24 }}
                gap={1.5}
                alignItems='baseline'
              >
                <Text2 sx={{ color: 'rgba(255,255,255,0.5)' }}>创建时间</Text2>
                <TextWhite2>
                  {orgInfo?.created_at ? dayjs.unix(orgInfo?.created_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
                </TextWhite2>
              </Stack>
            </Stack>
          </Box>
          <Stack alignItems='flex-end' sx={{ pr: 3 }}>
            <Tabs value={value} onChange={handleChange}>
              <Tab
                disableRipple
                value={TabValue.Base}
                icon={<Icon type='icon-kongjianguanli' />}
                iconPosition='start'
                label='空间管理'
              />
              <Tab
                disableRipple
                value={TabValue.Order}
                icon={<Icon type='icon-dingdanguanli' />}
                iconPosition='start'
                label='订单管理'
              />
            </Tabs>
          </Stack>
        </Card>

        {value === TabValue.Base && (
          <>
            <UserManage />
            <APIToken />
            <ExitSpace
              onExit={() => {
                setExitSpaceModalVisible(true)
              }}
            />
            <DissolutionSpace
              onDissolve={() => {
                setDissolutionSpaceModalVisible(true)
              }}
            />
          </>
        )}
        {value === TabValue.Order && <Order />}
      </Stack>
    </Box>
  )
}

export default Personal
